import { FilterGroup } from "@components/common/data-grid/adm-data-grid-types";
import { GridFilterModel, GridLogicOperator } from "@mui/x-data-grid-pro";
import { FilterSelection } from "@utils/track-event/event-types";

/** @returns whether all, some or none of the options of a filter are selected */
export function getFilterSelection(
  selectedCount: number,
  optionCount: number
): FilterSelection {
  if (selectedCount === 0) {
    return "none";
  }

  return selectedCount >= optionCount ? "all" : "some";
}

/** @returns the ids of the options currently selected within the given group */
export function getSelectedOptionIds(
  group: FilterGroup,
  activeFilters: GridFilterModel | undefined
): string[] {
  const item = activeFilters?.items?.find(({ id }) => id === group.id);
  return Array.isArray(item?.value) ? (item.value as string[]) : [];
}

/**
 * Replaces the selection of a single group in the active filters.
 *
 * The group is represented by one filter item holding all its selected option ids, or dropped when
 * nothing is selected. The item's operator combines those options with OR, while the model keeps
 * the groups combined with AND.
 */
export function setGroupSelection(
  group: FilterGroup,
  selectedIds: string[],
  activeFilters: GridFilterModel | undefined
): GridFilterModel {
  const otherItems =
    activeFilters?.items?.filter(({ id }) => id !== group.id) ?? [];

  const nextItems =
    selectedIds.length > 0
      ? [
          ...otherItems,
          {
            id: group.id,
            field: group.field,
            operator: group.operator,
            value: selectedIds,
          },
        ]
      : otherItems;

  return {
    ...activeFilters,
    items: nextItems,
    logicOperator: GridLogicOperator.And,
  };
}

/**
 * Builds the filter model to apply when the table first loads, from the options each group flags as
 * default-selected. Returns undefined when no option is a default, so the table starts unfiltered.
 */
export function getDefaultFilterModel(
  groups: FilterGroup[]
): GridFilterModel | undefined {
  const items = groups
    .map((group) => ({
      group,
      selectedIds: group.options
        .filter((option) => option.isDefaultSelected)
        .map((option) => option.id),
    }))
    .filter(({ selectedIds }) => selectedIds.length > 0)
    .map(({ group, selectedIds }) => ({
      id: group.id,
      field: group.field,
      operator: group.operator,
      value: selectedIds,
    }));

  if (items.length === 0) {
    return undefined;
  }

  return {
    items,
    logicOperator: GridLogicOperator.And,
  };
}
